import type { FC, ReactNode } from 'react'
import type { ContactTopic } from '@/constants/contact'
import type { Plan } from '@/constants/home'

import { useState } from 'react'
import { Check, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClasses } from '@/components/generals/button'
import { Scallop } from '@/components/generals/scallop'
import { SectionHeading } from '@/components/generals/section-heading'
import { Sticker } from '@/components/generals/sticker'
import { PLANS } from '@/constants/home'

type Billing = 'monthly' | 'yearly'

interface PricingProps {
    onChoose: (topic: ContactTopic) => void
}

const formatPrice = (value: number): string => `$${value.toLocaleString('en-US')}`

const priceFor = (plan: Plan, billing: Billing): { amount: string, unit: string, note: string } => {
    if (plan.oneTime) {
        return { amount: formatPrice(plan.oneTime), unit: 'per project', note: 'One-time fee, paid in two parts' }
    }

    if (billing === 'yearly' && plan.yearly) {
        return { amount: formatPrice(plan.yearly), unit: '/month', note: `Billed yearly at ${formatPrice(plan.yearly * 12)}` }
    }

    return { amount: formatPrice(plan.monthly ?? 0), unit: '/month', note: 'Billed monthly, cancel anytime' }
}

export const Pricing: FC<PricingProps> = ({ onChoose }): ReactNode => {
    const [billing, setBilling] = useState<Billing>('monthly')

    return (
        <section id='pricing' aria-labelledby='pricing-title' className='relative bg-cream py-24 sm:py-32'>
            <Scallop className='text-cream' />

            <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                <SectionHeading
                    id='pricing-title'
                    lines={['Pick a plan,', 'skip the haggling']}
                    sticker='Pricing'
                    stickerShape='cloud'
                    stickerColor='text-peach'
                />

                <div className='mt-10 flex flex-col items-center gap-3'>
                    <div role='group' aria-label='Billing period' className='inline-flex rounded-full border-2 border-plum bg-cream-deep p-1.5 shadow-[0_4px_0_0_var(--color-plum)]'>
                        {(['monthly', 'yearly'] as Billing[]).map((option) => (
                            <button
                                key={option}
                                type='button'
                                aria-pressed={billing === option}
                                onClick={() => setBilling(option)}
                                className={cn(
                                    'rounded-full px-6 py-2 font-heading text-lg tracking-wide capitalize transition-all duration-300',
                                    billing === option ? 'scale-105 bg-plum text-cream animate-jelly' : 'text-plum hover:bg-butter'
                                )}
                            >
                                {option}
                            </button>
                        ))}
                    </div>

                    <p className='flex items-center gap-2 text-sm font-medium text-plum/75'>
                        <Sparkles className='size-4 text-coral' aria-hidden='true' />
                        Save 20% on monthly plans when you pay yearly
                    </p>
                </div>

                <ul className='mt-14 space-y-8'>
                    {PLANS.map((plan, planIndex) => {
                        const price = priceFor(plan, billing)

                        return (
                            <li
                                key={plan.id}
                                className={cn(
                                    'group relative grid gap-8 rounded-[2.5rem] border-2 border-plum p-7 shadow-[0_6px_0_0_var(--color-plum)] sm:p-10 lg:grid-cols-[1.35fr_1fr] lg:gap-12',
                                    planIndex % 2 === 0 ? 'reveal-left' : 'reveal-right',
                                    plan.color
                                )}
                            >
                                {plan.popular && (
                                    <Sticker
                                        label='Most popular'
                                        shape='burst'
                                        colorClassName='text-butter'
                                        className='absolute -top-9 -right-2 size-24 rotate-12 text-base motion-safe:animate-wiggle sm:-right-6 sm:size-28'
                                    />
                                )}

                                <div>
                                    <h3 className='font-heading text-[clamp(2.25rem,4vw,3.25rem)] leading-none text-plum'>
                                        {plan.name}
                                        {plan.popular && <span className='sr-only'> (most popular)</span>}
                                    </h3>

                                    <p className='mt-2 text-lg text-plum/80'>
                                        {plan.description}
                                    </p>

                                    <ul className='mt-7 grid gap-x-6 gap-y-3 sm:grid-cols-2'>
                                        {plan.features.map((feature, featureIndex) => (
                                            <li key={feature} className='flex items-start gap-3 font-medium text-plum'>
                                                <span
                                                    className='mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-plum text-cream transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:scale-125 group-hover:-rotate-12'
                                                    style={{ transitionDelay: `${featureIndex * 40}ms` }}
                                                >
                                                    <Check className='size-3.5' strokeWidth={3} aria-hidden='true' />
                                                </span>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className='flex flex-col justify-between gap-6 rounded-[2rem] border-2 border-plum bg-cream p-6 sm:p-8'>
                                    <div>
                                        <p key={`${plan.id}-${billing}`} className='flex items-end gap-2 animate-in fade-in slide-in-from-bottom-2 duration-300'>
                                            <span className='font-heading text-[clamp(3.25rem,6vw,4.5rem)] leading-none text-plum'>
                                                {price.amount}
                                            </span>

                                            <span className='pb-2 font-heading text-xl text-plum/70'>
                                                {price.unit}
                                            </span>
                                        </p>

                                        <p className='mt-2 text-sm text-plum/70'>
                                            {price.note}
                                        </p>
                                    </div>

                                    <a
                                        href='#contact'
                                        onClick={() => onChoose(plan.topic)}
                                        className={buttonClasses('primary', 'md', 'w-full')}
                                    >
                                        {`Choose ${plan.name}`}
                                    </a>

                                    <p className='border-t-2 border-dashed border-plum/15 pt-5 text-sm leading-relaxed text-plum/80'>
                                        <span className='font-heading text-base tracking-wide text-plum'>Best for: </span>
                                        {plan.bestFor}
                                    </p>
                                </div>
                            </li>
                        )
                    })}
                </ul>

                <p className='mt-10 text-center text-plum/75'>
                    Need something different?{' '}
                    <a href='#contact' onClick={() => onChoose('Something else')} className='font-semibold text-plum underline decoration-2 underline-offset-4 hover:decoration-coral'>
                        Ask for a custom quote
                    </a>
                </p>
            </div>
        </section>
    )
}
